import { upsertRatchetKeys, getRatchetingKeys } from '../db/RatchetedConversationKeys';
import { getSDK as getUnlinkSDK } from '../sdk/UnlinkInstance';
import bs58 from 'bs58';
import { loadLocalKeys } from '../db/KeyStorageManager';
import { getHandshakeBySenderViewKey } from '../db/HandshakeRequests';
import { sendInstructionsWithRetry} from '../utils/SendInstructions';
import { FEES, MESSAGES_TYPES } from '../Config';
import { Messages } from "../db";
import { createMessageObject } from "../types/Message";
import dispatchToChat from "../services/message/DispatchToChat";

export async function initiateHandshake({
  conversationId,
  contactNextViewPublicKey,
  contactIdentityViewPublicKey,
  kyberSecret,
  message,
  userId, 
  password,
  salt,
}) {
  const unlinkSdk = getUnlinkSDK();

  // local keys
  const { 
    viewPublicKey: localViewPublicKey, 
    viewPrivateKey: localViewPrivateKey, 
  } = await loadLocalKeys({userId, password, salt});

  // generate filter tag
  const filterTag = unlinkSdk.deriveRandomFilterTag({
    viewKey: contactNextViewPublicKey || contactIdentityViewPublicKey,
    bits: 3 ,
  });

  // create new keypair for ratchet
  const {
    viewPublicKey: localNextViewPublicKey,
    viewPrivateKey: localNextViewPrivateKey,
    kyberPublicKey: sessionNextKyberPublicKey,
    kyberPrivateKey: sessionNextKyberPrivateKey,
  } = await unlinkSdk.generateUnlinkKeypair();

  // create solana transaction instructions
  const { instructions, messageId } = await unlinkSdk.createHandshakeSendInstructions({
    message,
    receiverViewPublicKey: contactNextViewPublicKey || contactIdentityViewPublicKey,
    senderViewPublicKey: localViewPublicKey,
    senderViewPrivateKey: localViewPrivateKey,
    senderNextViewPublicKey: localNextViewPublicKey,
    sessionNextKyberPublicKey,
    spamMitigationFeeLamports: FEES.SPAM_MITIGATION_LAMPORTS,
    conversationId,
    kyberSecret,
    filterTag,
  });

  // dispatch own message
  dispatchToChat({
    message: { 
      parsed: {
        kyberPublicKey: sessionNextKyberPublicKey,
        message: message,
        version: 1,
      },
      receiverViewPublicKey: contactNextViewPublicKey || contactIdentityViewPublicKey,
      senderViewPublicKey: localViewPublicKey,
      senderNextViewPublicKey: localNextViewPublicKey,
      conversationId,
      messageId,
      messageType: MESSAGES_TYPES.MESSAGE_TYPE_HANDSHAKE_REQUEST,
    },
    receiverIdentityViewPublicKey: contactIdentityViewPublicKey,
    userId, 
    password, 
    salt,
  });

  // send instructions to solana chain
  await sendInstructionsWithRetry({
    instructions: instructions,
    networkFeeLamports: FEES.NETWORK_FEE_LAMPORTS,
    password,
    userId,
    salt,
  });

  // store new keys for ratcheting
  await upsertRatchetKeys({
    conversationId,
    contactIdentityViewPublicKey,
    localNextViewPublicKey,
    localNextViewPrivateKey,
    sessionNextKyberPublicKey,
    sessionNextKyberPrivateKey,
    userId,
    password,
  });
}

export async function confirmHandshake({ 
  conversationId, 
  contactNextViewPublicKey,
  contactIdentityViewPublicKey,
  sessionKyberPublicKey,
  kyberSecret,
  password, 
  userId, 
  salt, 
}) {
  const unlinkSdk = getUnlinkSDK();
  const timestamp = Date.now();

  // local keys
  const {
    viewPublicKey: localViewPublicKey,
    viewPrivateKey: localViewPrivateKey,
  } = await loadLocalKeys({ userId, password, salt});
  
  // generate filter tag
  const filterTag = unlinkSdk.deriveRandomFilterTag({
    viewKey: contactNextViewPublicKey || contactIdentityViewPublicKey,
    bits: 3 ,
  });

  const keypair = await unlinkSdk.generateUnlinkKeypair();
  const localNextViewPublicKey = keypair.viewPublicKey;
  const localNextViewPrivateKey = keypair.viewPrivateKey;

  // build solana transaction instructions
  const { 
    instructions, 
    messageId,
    kyberSecret: newKyberSecret, 
    kyberCiphertext: newKyberCiphertext,
  } = await unlinkSdk.createHandshakeConfirmInstructions({
    receiverViewPublicKey: contactNextViewPublicKey || contactIdentityViewPublicKey,
    sessionKyberPublicKey,
    senderViewPublicKey: localViewPublicKey,
    senderViewPrivateKey: localViewPrivateKey,
    senderNextViewPublicKey: localNextViewPublicKey,
    spamMitigationFeeLamports: FEES.SPAM_MITIGATION_LAMPORTS,
    conversationId,
    kyberSecret,
    filterTag,
  });

  // dispatch own message
  dispatchToChat({
    message: { 
      parsed: {
        version: 1,
        kyberCiphertext: newKyberCiphertext,
      },
      kyberKeys: {
        kyberPublicKey: sessionKyberPublicKey,
        kyberPrivateKey: null,
        kyberSecret: newKyberSecret,
      },
      receiverViewPublicKey: contactNextViewPublicKey || contactIdentityViewPublicKey,
      senderViewPublicKey: localViewPublicKey,
      senderNextViewPublicKey: localNextViewPublicKey,
      conversationId,
      messageId,
      messageType: MESSAGES_TYPES.MESSAGE_TYPE_HANDSHAKE_CONFIRM,
    }, 
    receiverIdentityViewPublicKey: contactIdentityViewPublicKey,
    userId, 
    password, 
    salt,
  });

  // send instructions to solana chain
  await sendInstructionsWithRetry({
    instructions: instructions,
    networkFeeLamports: FEES.NETWORK_FEE_LAMPORTS,
    password,
    userId,
    salt,
  });

  // store new viewkeys for ratcheting
  await upsertRatchetKeys({
    conversationId,
    contactIdentityViewPublicKey,
    localNextViewPublicKey,
    localNextViewPrivateKey,
    sessionNextKyberPublicKey: sessionKyberPublicKey,
    sessionNextKyberSecret: newKyberSecret,
    userId,
    password,
  });
}

/**
 * Determines the handshake status between the current user and a contact.
 *
 * Status can be:
 * - "none": No handshake activity — no local keys, no contact keys.
 * - "waiting": We sent local keys but haven't received contact's kyber secret.
 * - "incoming_unconfirmed": We received contact's view key, but haven't sent our keys yet.
 * - "complete": Both sides have exchanged and stored required keys.
 *
 * @param {Object} params
 * @param {Uint8Array} params.conversationId - Unique conversation ID (Uint8Array)
 * @param {Uint8Array} params.contactIdentityViewPublicKey - Contact's identity view public key (Uint8Array)
 * @param {string} params.password - Current user's password
 * @param {string} params.userId - Current user's ID
 * @returns {Promise<{ status: string }>}
 */
export async function checkHandshakeStatus({
  conversationId,
  contactIdentityViewPublicKey,
  password,
  userId,
}) {
  if (!(conversationId instanceof Uint8Array)) {
    throw new Error("conversationId must be a Uint8Array");
  }
  if (!(contactIdentityViewPublicKey instanceof Uint8Array)) {
    throw new Error("contactIdentityViewPublicKey must be a Uint8Array");
  }

  const ratchetingKeys = await getRatchetingKeys({
    conversationId,
    contactIdentityViewPublicKey,
    userId,
    password,
  });

  const hasContactNextViewPublicKey = !!ratchetingKeys?.contactNextViewPublicKey;
  const hasContactNextKyberSecret = !!ratchetingKeys?.contactNextKyberSecret;
  const hasLocalViewKeys = ratchetingKeys?.localViewKeys?.length > 0;
  const hasSessionKyberSecrets = 
    Array.isArray(ratchetingKeys?.sessionKyberKeys) &&
    ratchetingKeys.sessionKyberKeys.some(
      (k) => k.kyberSecret instanceof Uint8Array && k.kyberSecret.length > 0
    );

  let status;

  if (!hasLocalViewKeys && !hasSessionKyberSecrets && !hasContactNextViewPublicKey && !hasContactNextKyberSecret) {
    status = "local_none__contact_none";
  } else if (hasLocalViewKeys && !hasSessionKyberSecrets && !hasContactNextViewPublicKey && !hasContactNextKyberSecret) {
    status = "local_view__contact_none";
  } else if (hasLocalViewKeys && hasSessionKyberSecrets && !hasContactNextViewPublicKey && !hasContactNextKyberSecret) {
    status = "local_view_kyber__contact_none";
  } else if (!hasLocalViewKeys && !hasSessionKyberSecrets && hasContactNextViewPublicKey && !hasContactNextKyberSecret) {
    status = "local_none__contact_view";
  } else if (hasLocalViewKeys && !hasSessionKyberSecrets && hasContactNextViewPublicKey && !hasContactNextKyberSecret) {
    status = "local_view__contact_view";
  } else if (hasLocalViewKeys && hasSessionKyberSecrets && hasContactNextViewPublicKey && !hasContactNextKyberSecret) {
    status = "local_view_kyber__contact_view";
  } else if (!hasLocalViewKeys && !hasSessionKyberSecrets && hasContactNextViewPublicKey && hasContactNextKyberSecret) {
    status = "local_none__contact_view_kyber";
  } else if (hasLocalViewKeys && !hasSessionKyberSecrets && hasContactNextViewPublicKey && hasContactNextKyberSecret) {
    status = "local_view__contact_view_kyber";
  } else if (hasLocalViewKeys && hasSessionKyberSecrets && hasContactNextViewPublicKey && hasContactNextKyberSecret) {
    status = "local_view_kyber__contact_view_kyber";
  }

  return {
    status,
    contactIdentityViewPublicKey,
    contactNextViewPublicKey: ratchetingKeys?.contactNextViewPublicKey,
    localViewKeys: ratchetingKeys?.localViewKeys || [],
    sessionKyberKeys: ratchetingKeys?.sessionKyberKeys || [],
  };
}